import React from "react";
import { isImageUrl } from "../utils/videoUtils";
import { useNavigate } from "react-router-dom";

interface GalleryVideo {
  id: string;
  title: string;
  thumbnail: string;
  videoUrl: string;
  prompt: string;
  character: string;
  dimensions: { width: number; height: number };
}

interface VideoGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: GalleryVideo | null;
}

const VideoGalleryModal: React.FC<VideoGalleryModalProps> = ({
  isOpen,
  onClose,
  video,
}) => {
  const navigate = useNavigate();

  const handleCreateSimilarVideo = () => {
    if (video) {
      // Navigate to text-to-video page with the prompt pre-filled
      navigate("/video/t2v", {
        state: {
          prefilledPrompt: video.prompt,
        },
      });
      onClose();
    }
  };

  if (!isOpen || !video) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {video.title}
            </h2>
            <p className="text-sm text-gray-600">{video.character}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-6 h-6 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Check if video has no prompt (special no-filter example) */}
          {!video.prompt ? (
            // Full-width video only layout
            <div className="flex justify-center">
              <div className="w-full max-w-lg">
                <div
                  className="rounded-lg overflow-hidden bg-gray-900"
                  style={{
                    aspectRatio: `${video.dimensions.width} / ${video.dimensions.height}`,
                  }}
                >
                  {isImageUrl(video.videoUrl) ? (
                    <img
                      src={video.videoUrl}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      className="w-full h-full object-cover"
                      controls
                      muted
                      playsInline
                      webkit-playsinline="true"
                      preload="metadata"
                      onError={(e) => {
                        console.error("Gallery video failed to load:", e);
                        console.error("Video URL:", video.videoUrl);
                      }}
                      onLoadedData={() => {
                        console.log("Gallery video loaded successfully");
                      }}
                    >
                      <source src={video.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Two-column layout with video and prompt details
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Media Player */}
              <div className="space-y-4">
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-900">
                  {isImageUrl(video.videoUrl) ? (
                    <img
                      src={video.videoUrl}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      className="w-full h-full object-cover"
                      controls
                      muted
                      playsInline
                      webkit-playsinline="true"
                      preload="metadata"
                      onError={(e) => {
                        console.error("Gallery video failed to load:", e);
                        console.error("Video URL:", video.videoUrl);
                      }}
                      onLoadedData={() => {
                        console.log("Gallery video loaded successfully");
                      }}
                    >
                      <source src={video.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              </div>

              {/* Prompt Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Prompt Used
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-700 leading-relaxed">
                      {video.prompt}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Character
                  </h4>
                  <p className="text-gray-600">{video.character}</p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={handleCreateSimilarVideo}
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Create Similar Video
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoGalleryModal;
